import type { ApiResult } from '../../api/client.js';

/**
 * Bulk parent import (2026-10-23) — the client-side half of the one-POST TXT
 * import: pre-flight limits that mirror the api route, failure-reason mapping
 * and the Turkish labels for the per-line error codes. Pure helpers; the
 * modal owns all state.
 */

/** Per-route body limit on both import routes (PARENT_IMPORT_BODY_LIMIT_BYTES). */
export const PARENT_IMPORT_MAX_BODY_BYTES = 16 * 1024 * 1024;
/** Default server line cap (PARENT_IMPORT_MAX_LINES); counted in data lines. */
export const PARENT_IMPORT_MAX_LINES = 250_000;

export const IMPORT_TOO_LARGE_MESSAGE = 'Dosya çok büyük (en fazla 16 MB)';
export const IMPORT_CONFLICT_MESSAGE =
  'Bu sağlayıcı için başka bir içe aktarma sürüyor. Bitince aynı dosyayı tekrar yükleyin; eklenmiş satırlar atlanır.';
export const IMPORT_TIMEOUT_MESSAGE =
  'Sunucu yanıtı zaman aşımına uğradı; içe aktarma sürüyor olabilir. 1-2 dk sonra aynı dosyayı tekrar yükleyin — eklenenler atlanır.';
export const IMPORT_STOPPED_EARLY_MESSAGE =
  'İçe aktarma art arda veritabanı hataları yüzünden erken durduruldu — dosyayı tekrar yükleyin; eklenenler atlanır.';
export const GMXBOX_PROBE_HINT = 'Hesaplar kimlik doğrulaması (probe) tamamlanınca satışa girer.';

const LINE_FORMAT = new Intl.NumberFormat('tr-TR');

export function tooManyLinesMessage(maxLines: number = PARENT_IMPORT_MAX_LINES): string {
  return `En fazla ${LINE_FORMAT.format(maxLines)} satır`;
}

/** Data lines: blank lines and `#` comments do not count (same rule as the api parser). */
export function countDataLines(text: string): number {
  let n = 0;
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (line.length > 0 && !line.startsWith('#')) n += 1;
  }
  return n;
}

/**
 * Checks the request before it is sent. Returns the Turkish message that
 * blocks the send, or null. The size is the UTF-8 length of the exact JSON
 * body `callAdminApi` will send, so escaping (`"` → `\"`) counts.
 */
export function preflightImport(body: { readonly text: string; readonly aliasDomain?: string }): string | null {
  if (countDataLines(body.text) > PARENT_IMPORT_MAX_LINES) return tooManyLinesMessage();
  const bytes = new TextEncoder().encode(JSON.stringify(body)).length;
  if (bytes > PARENT_IMPORT_MAX_BODY_BYTES) return IMPORT_TOO_LARGE_MESSAGE;
  return null;
}

/**
 * The import routes answer `{error, detail}` without a `reason`, so the shared
 * client reduces them to `http_<status>`. Promote `detail` (plus `maxLines` for
 * too_many_lines) into the reason so the modal can name the failure.
 */
export function withImportFailureReason<T>(res: ApiResult<T>): ApiResult<T> {
  if (res.kind !== 'error' || !/^http_\d+$/.test(res.reason)) return res;
  const detail = res.body?.detail;
  if (typeof detail !== 'string' || detail.length === 0) return res;
  const maxLines = res.body?.maxLines;
  const reason =
    detail === 'too_many_lines' && typeof maxLines === 'number' ? `too_many_lines:${maxLines}` : detail;
  return { ...res, reason };
}

/** Maps a failed import's reason to what the operator should do next. */
export function describeImportFailure(reason: string): string {
  if (reason === 'import_already_running' || reason === 'http_409') return IMPORT_CONFLICT_MESSAGE;
  if (reason === 'http_413') return IMPORT_TOO_LARGE_MESSAGE;
  if (reason === 'http_504' || reason === 'http_524' || reason.startsWith('network:')) {
    return IMPORT_TIMEOUT_MESSAGE;
  }
  if (reason === 'too_many_lines') return tooManyLinesMessage();
  const cap = /^too_many_lines:(\d+)$/.exec(reason);
  if (cap) return tooManyLinesMessage(Number(cap[1]));
  return reason;
}

const ERROR_CODE_LABELS: Readonly<Record<string, string>> = {
  invalid_format: 'Biçim hatalı',
  invalid_domain: 'Alan adı bu sağlayıcıya ait değil',
  invalid_app_password: 'Uygulama şifresi geçersiz',
  refresh_token_required: 'Yenileme anahtarı (token) eksik',
  run_aborted: 'İçe aktarma durduruldu — dosyayı tekrar yükleyin',
  storage_error: 'Kayıt hatası',
};
const CHUNK_FAILED_LABEL = 'Geçici veritabanı hatası — dosyayı tekrar yükleyin';
/**
 * D-263 — a line skipped on purpose: the mailbox is already registered under
 * the other GMX line (`handle_exists_under_sibling_provider:<gmx|gmxbox>`, D9
 * skip + report). Never a storage error.
 */
const SIBLING_HANDLE_PREFIX = 'handle_exists_under_sibling_provider';
const SIBLING_PROVIDER_LABELS: Readonly<Record<string, string>> = {
  gmx: 'gmx.com Parent Mailler',
  gmxbox: 'GMX mailbox',
};

/**
 * Turkish label for an `errorsByCode` key or an `errorDetails[].error` value.
 * A free-form value is a storage message (≤120 chars, no credential): it is
 * shown after the storage label.
 */
export function importErrorLabel(code: string): string {
  const known = ERROR_CODE_LABELS[code];
  if (known) return known;
  if (code.startsWith('chunk_failed:') || code === 'chunk_failed') return CHUNK_FAILED_LABEL;
  if (code === SIBLING_HANDLE_PREFIX || code.startsWith(`${SIBLING_HANDLE_PREFIX}:`)) {
    const sibling = code.slice(SIBLING_HANDLE_PREFIX.length + 1);
    const where = SIBLING_PROVIDER_LABELS[sibling] ?? 'diğer GMX';
    return `Bu adres ${where} hattında zaten kayıtlı — atlandı`;
  }
  return `${ERROR_CODE_LABELS.storage_error}: ${code}`;
}

/** `850 ms`, `12,3 sn`, `4 dk 05 sn`. */
export function formatImportDuration(ms: number): string {
  if (ms < 1000) return `${Math.max(0, Math.round(ms))} ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toLocaleString('tr-TR', { maximumFractionDigits: 1 })} sn`;
  const whole = Math.round(seconds);
  return `${Math.floor(whole / 60)} dk ${String(whole % 60).padStart(2, '0')} sn`;
}
